/**
 * FASE 2 - Fatturazione Interna
 * Generazione PDF con numerazione automatica
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, Plus, Download, Eye, Search, Filter, Calendar,
  ChevronLeft, ChevronRight, Euro, Printer, Send, Trash2,
  Edit2, X, Check, RefreshCw, Building, User, Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' }
];

const INVOICE_STATUS = [
  { value: 'bozza', label: 'Bozza', color: 'gray' },
  { value: 'emessa', label: 'Emessa', color: 'blue' },
  { value: 'inviata', label: 'Inviata', color: 'amber' },
  { value: 'pagata', label: 'Pagata', color: 'green' },
  { value: 'annullata', label: 'Annullata', color: 'red' }
];

const PAYMENT_METHODS = [
  { value: 'contanti', label: 'Contanti' },
  { value: 'bonifico', label: 'Bonifico Bancario' },
  { value: 'carta', label: 'Carta di Credito/Debito' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'satispay', label: 'Satispay' },
  { value: 'booking', label: 'Booking.com' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'assegno', label: 'Assegno' },
  { value: 'altro', label: 'Altro' }
];

export default function EconomicInvoices() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({ count: 0, totale: 0, incassato: 0, da_incassare: 0 });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null); // null = tutto l'anno
  const [filterStato, setFilterStato] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Email Modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [emailForm, setEmailForm] = useState({
    email: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Ricerca ospiti
  const [guestSearch, setGuestSearch] = useState('');
  const [guestResults, setGuestResults] = useState([]);
  const [searchingGuests, setSearchingGuests] = useState(false);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [selectedGuestSoggiorni, setSelectedGuestSoggiorni] = useState([]);
  
  // Impostazioni aziendali per anteprima
  const [aziendaSettings, setAziendaSettings] = useState({
    azienda_nome: '',
    azienda_sottotitolo: '',
    azienda_indirizzo: '',
    azienda_cap: '',
    azienda_citta: '',
    azienda_provincia: '',
    azienda_codice_fiscale: '',
    azienda_partita_iva: '',
    azienda_telefono: '',
    azienda_email: '',
    azienda_sito_web: '',
    azienda_logo_url: '',
    // Template Email
    email_fattura_oggetto: '',
    email_fattura_corpo: ''
  });

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2
  ];
  
  // Fetch impostazioni aziendali
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/financial/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAziendaSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [token]);

  // Cerca ospiti per auto-compilazione
  const searchGuests = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setGuestResults([]);
      setShowGuestDropdown(false);
      return;
    }
    
    setSearchingGuests(true);
    try {
      const response = await axios.get(
        `${API}/admin/economic/search-guests?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGuestResults(response.data.guests || []);
      setShowGuestDropdown(true);
    } catch (error) {
      console.error('Error searching guests:', error);
      setGuestResults([]);
    } finally {
      setSearchingGuests(false);
    }
  }, [token]);

  // Debounce per la ricerca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (guestSearch) {
        searchGuests(guestSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [guestSearch, searchGuests]);

  // Seleziona un ospite e compila i campi
  const selectGuest = (guest) => {
    // Compila dati cliente
    const newFormData = {
      ...formData,
      cliente_nome: guest.nome || '',
      cliente_indirizzo: guest.indirizzo || '',
      cliente_cf: guest.codice_fiscale || '',
      cliente_email: guest.email || ''
    };
    
    // Se ha soggiorni, usa automaticamente il primo (più recente)
    if (guest.soggiorni && guest.soggiorni.length > 0) {
      const soggiorno = guest.soggiorni[0]; // Primo soggiorno (più recente)
      
      // Formatta la data per la descrizione
      let dataDesc = '';
      if (soggiorno.data_arrivo) {
        try {
          const d = new Date(soggiorno.data_arrivo);
          dataDesc = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
          dataDesc = soggiorno.data_arrivo;
        }
      }
      
      const descrizione = `${soggiorno.notti || 1} PERNOTTAMENTO ${soggiorno.adulti || 1} ADULTO - ${dataDesc}`;
      
      newFormData.importo = soggiorno.importo_netto || soggiorno.importo_lordo || 0;
      newFormData.descrizione = descrizione;
      newFormData.modalita_pagamento = soggiorno.canale || 'DIRETTO';
      newFormData.booking_id = soggiorno.booking_id;
      
      // Se ha più soggiorni, mostra la lista per eventuale cambio
      if (guest.soggiorni.length > 1) {
        setSelectedGuestSoggiorni(guest.soggiorni);
        toast.success(`${guest.nome} - soggiorno più recente inserito (${guest.soggiorni.length} disponibili)`);
      } else {
        setSelectedGuestSoggiorni([]);
        toast.success(`Dati di ${guest.nome} e soggiorno inseriti - €${(newFormData.importo || 0).toFixed(2)}`);
      }
    } else {
      setSelectedGuestSoggiorni([]);
      toast.success(`Dati di ${guest.nome} inseriti`);
    }
    
    setFormData(newFormData);
    setGuestSearch('');
    setGuestResults([]);
    setShowGuestDropdown(false);
  };

  // Seleziona un soggiorno specifico per compilare importi
  const selectSoggiorno = (soggiorno) => {
    // Formatta la data per la descrizione
    let dataDesc = '';
    if (soggiorno.data_arrivo) {
      try {
        const d = new Date(soggiorno.data_arrivo);
        dataDesc = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch {
        dataDesc = soggiorno.data_arrivo;
      }
    }
    
    const descrizione = `${soggiorno.notti || 1} PERNOTTAMENTO ${soggiorno.adulti || 1} ADULTO - ${dataDesc}`;
    
    setFormData(prev => ({
      ...prev,
      importo: soggiorno.importo_netto || soggiorno.importo_lordo || 0,
      descrizione: descrizione,
      modalita_pagamento: soggiorno.canale || 'DIRETTO',
      booking_id: soggiorno.booking_id
    }));
    
    setSelectedGuestSoggiorni([]);
    toast.success(`Soggiorno del ${dataDesc} selezionato - €${(soggiorno.importo_netto || soggiorno.importo_lordo || 0).toFixed(2)}`);
  };

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ anno: selectedYear });
      if (selectedMonth) params.append('mese', selectedMonth);
      if (filterStato) params.append('stato', filterStato);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(
        `${API}/admin/economic/invoices?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setInvoices(response.data.invoices || []);
      setTotals(response.data.totals || { count: 0, totale: 0, incassato: 0, da_incassare: 0 });
    } catch (error) {
      console.error('Error fetching invoices:', error);
      // Se l'endpoint non esiste ancora, mostriamo dati vuoti
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth, filterStato, searchQuery]);

  useEffect(() => {
    if (token) {
      fetchInvoices();
      fetchSettings();
    }
  }, [token, fetchInvoices, fetchSettings]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const openCreateModal = () => {
    setEditingInvoice(null);
    const today = new Date();
    const numero = `${today.getFullYear()}/${String(invoices.length + 1).padStart(4, '0')}`;
    setFormData({
      numero: numero,
      data_emissione: today.toISOString().split('T')[0],
      cliente_nome: '',
      cliente_indirizzo: '',
      cliente_cf: '',
      cliente_piva: '',
      descrizione: '',
      importo: '',
      iva: 0,
      stato: 'bozza',
      note: '',
      modalita_pagamento: 'contanti',
      acconto_importo: '',
      acconto_data: '',
      acconto_metodo: ''
    });
    setShowModal(true);
  };

  const openEditModal = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      numero: invoice.numero,
      data_emissione: invoice.data_emissione,
      data_scadenza: invoice.data_scadenza,
      cliente_nome: invoice.cliente_nome,
      cliente_indirizzo: invoice.cliente_indirizzo,
      cliente_cf: invoice.cliente_cf,
      cliente_piva: invoice.cliente_piva,
      descrizione: invoice.descrizione,
      importo: invoice.importo,
      iva: invoice.iva || 0,
      stato: invoice.stato,
      note: invoice.note,
      booking_id: invoice.booking_id,
      modalita_pagamento: invoice.modalita_pagamento || 'contanti',
      acconto_importo: invoice.acconto_importo || '',
      acconto_data: invoice.acconto_data || '',
      acconto_metodo: invoice.acconto_metodo || ''
    });
    setShowModal(true);
  };

  // Funzioni per invio email
  const openEmailModal = (invoice) => {
    setEmailInvoice(invoice);
    
    // Usa template dalle impostazioni o default
    let oggetto = aziendaSettings.email_fattura_oggetto || `Ricevuta Fiscale nr. {{numero}} - {{azienda_nome}}`;
    let corpo = aziendaSettings.email_fattura_corpo || `Gentile {{cliente_nome}},

in allegato trova la ricevuta fiscale nr. {{numero}} relativa al suo soggiorno.

Importo totale: {{totale}}
{{#if acconto}}Acconto versato: {{acconto}} - Saldo: {{saldo}}{{/if}}

Per qualsiasi chiarimento non esiti a contattarci.

Cordiali saluti,
{{azienda_nome}}
Tel: {{azienda_telefono}}
{{azienda_email}}`;

    // Sostituisci variabili
    const replacements = {
      '{{numero}}': invoice.numero || '',
      '{{cliente_nome}}': invoice.cliente_nome || '',
      '{{totale}}': formatCurrency(invoice.totale),
      '{{acconto}}': formatCurrency(invoice.acconto_importo || 0),
      '{{saldo}}': formatCurrency(invoice.saldo_dovuto || (invoice.totale - (invoice.acconto_importo || 0))),
      '{{data_emissione}}': formatDate(invoice.data_emissione),
      '{{azienda_nome}}': aziendaSettings.azienda_nome || 'La Maisonette di Paestum',
      '{{azienda_telefono}}': aziendaSettings.azienda_telefono || '',
      '{{azienda_email}}': aziendaSettings.azienda_email || ''
    };

    Object.keys(replacements).forEach(key => {
      oggetto = oggetto.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), replacements[key]);
      corpo = corpo.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), replacements[key]);
    });

    // Gestisci condizioni {{#if acconto}}...{{/if}}
    if (invoice.acconto_importo && invoice.acconto_importo > 0) {
      corpo = corpo.replace(/\{\{#if acconto\}\}(.*?)\{\{\/if\}\}/gs, '$1');
    } else {
      corpo = corpo.replace(/\{\{#if acconto\}\}(.*?)\{\{\/if\}\}/gs, '');
    }

    setEmailForm({
      email: '',
      subject: oggetto,
      message: corpo
    });
    setShowEmailModal(true);
  };

  const sendInvoiceEmail = async () => {
    if (!emailForm.email) {
      toast.error('Inserisci l\'email del destinatario');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.email)) {
      toast.error('Inserisci un\'email valida');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${API}/admin/economic/invoices/${emailInvoice.id}/send-email`,
        emailForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Fattura inviata a ${emailForm.email}`);
      setShowEmailModal(false);
      
      // Update invoice stato to 'inviata' if it was 'emessa'
      if (emailInvoice.stato === 'emessa') {
        await axios.put(
          `${API}/admin/economic/invoices/${emailInvoice.id}`,
          { ...emailInvoice, stato: 'inviata' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Errore invio email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSave = async () => {
    if (!formData.cliente_nome || !formData.importo) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        importo: parseFloat(formData.importo) || 0,
        iva: parseFloat(formData.iva) || 0,
        totale: (parseFloat(formData.importo) || 0) + (parseFloat(formData.iva) || 0),
        acconto_importo: parseFloat(formData.acconto_importo) || 0,
        saldo_dovuto: ((parseFloat(formData.importo) || 0) + (parseFloat(formData.iva) || 0)) - (parseFloat(formData.acconto_importo) || 0)
      };
      
      if (editingInvoice) {
        await axios.put(
          `${API}/admin/economic/invoices/${editingInvoice.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Fattura aggiornata');
      } else {
        await axios.post(
          `${API}/admin/economic/invoices`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Fattura creata');
      }
      setShowModal(false);
      fetchInvoices();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa fattura?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/economic/invoices/${invoice.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fattura eliminata');
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await axios.get(
        `${API}/admin/economic/invoices/${invoice.id}/pdf`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Fattura_${invoice.numero.replace('/', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF scaricato');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Funzionalità PDF in arrivo');
    }
  };

  const openPreview = (invoice) => {
    setPreviewInvoice(invoice);
    setShowPreview(true);
  };

  const prevMonth = () => {
    if (selectedMonth === null) {
      setSelectedMonth(12);
    } else if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === null) {
      setSelectedMonth(1);
    } else if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const getStatusColor = (stato) => {
    const s = INVOICE_STATUS.find(st => st.value === stato);
    return s?.color || 'gray';
  };

  return (
    <AdminLayout title="Fatture">
      <div className="space-y-4 pb-20 md:pb-4" data-testid="economic-invoices-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-cinzel text-xl md:text-2xl text-[#1A202C]">Fatture</h1>
            <p className="text-sm text-gray-500">Gestione fatture e documenti fiscali</p>
          </div>
          
          {/* Period Navigator */}
          <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1 w-full md:w-auto justify-center">
            <Button variant="ghost" size="sm" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-[140px] justify-center">
              <select
                value={selectedMonth || ''}
                onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
                className="border-0 bg-transparent font-medium text-center appearance-none cursor-pointer text-sm"
              >
                <option value="">Tutto anno</option>
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border-0 bg-transparent text-gray-500 appearance-none cursor-pointer text-sm"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button variant="ghost" size="sm" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Fatture Emesse</p>
              <p className="text-xl font-bold text-blue-600">{totals.count || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Totale Fatturato</p>
              <p className="text-xl font-bold text-gray-700">{formatCurrency(totals.totale)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Incassato</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(totals.incassato)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Da Incassare</p>
              <p className="text-xl font-bold text-amber-600">{formatCurrency(totals.da_incassare)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Cerca cliente, numero fattura..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
              data-testid="invoice-status-filter"
            >
              <option value="">Tutti gli stati</option>
              {INVOICE_STATUS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={fetchInvoices}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C5A059]"></div>
          </div>
        ) : invoices.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-4">Nessuna fattura trovata</p>
              <Button onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" />
                Crea Prima Fattura
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block">
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Numero</th>
                          <th className="text-left p-3 font-medium text-gray-600">Data</th>
                          <th className="text-left p-3 font-medium text-gray-600">Cliente</th>
                          <th className="text-left p-3 font-medium text-gray-600">Descrizione</th>
                          <th className="text-right p-3 font-medium text-gray-600">Importo</th>
                          <th className="text-right p-3 font-medium text-gray-600">IVA</th>
                          <th className="text-right p-3 font-medium text-gray-600">Totale</th>
                          <th className="text-center p-3 font-medium text-gray-600">Stato</th>
                          <th className="text-right p-3 font-medium text-gray-600">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="border-b hover:bg-gray-50" data-testid={`invoice-row-${invoice.id}`}>
                            <td className="p-3 font-mono text-blue-600">{invoice.numero}</td>
                            <td className="p-3 text-gray-600">{formatDate(invoice.data_emissione)}</td>
                            <td className="p-3">
                              <div className="font-medium text-gray-800">{invoice.cliente_nome}</div>
                              {invoice.cliente_piva && (
                                <div className="text-xs text-gray-500">P.IVA: {invoice.cliente_piva}</div>
                              )}
                            </td>
                            <td className="p-3 text-gray-600 max-w-[200px] truncate">{invoice.descrizione}</td>
                            <td className="p-3 text-right">{formatCurrency(invoice.importo)}</td>
                            <td className="p-3 text-right text-gray-500">{formatCurrency(invoice.iva)}</td>
                            <td className="p-3 text-right font-bold">{formatCurrency(invoice.totale)}</td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs bg-${getStatusColor(invoice.stato)}-100 text-${getStatusColor(invoice.stato)}-700`}>
                                {INVOICE_STATUS.find(s => s.value === invoice.stato)?.label || invoice.stato}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button variant="ghost" size="sm" onClick={() => openPreview(invoice)} title="Anteprima">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(invoice)} title="Scarica PDF" data-testid={`invoice-download-${invoice.id}`}>
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openEmailModal(invoice)} title="Invia Email" className="text-blue-600">
                                  <Mail className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openEditModal(invoice)} title="Modifica">
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(invoice)} className="text-red-500" title="Elimina">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {invoices.map((invoice) => (
                <Card key={invoice.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-mono text-blue-600 font-medium">{invoice.numero}</div>
                        <div className="text-xs text-gray-500">{formatDate(invoice.data_emissione)}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs bg-${getStatusColor(invoice.stato)}-100 text-${getStatusColor(invoice.stato)}-700`}>
                        {INVOICE_STATUS.find(s => s.value === invoice.stato)?.label || invoice.stato}
                      </span>
                    </div>
                    
                    <div className="font-medium text-gray-800 mb-2">{invoice.cliente_nome}</div>
                    
                    <div className="flex justify-between items-center border-t pt-2">
                      <div className="font-bold text-lg">{formatCurrency(invoice.totale)}</div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openPreview(invoice)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(invoice)}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEmailModal(invoice)} className="text-blue-600">
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(invoice)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Mobile FAB */}
        <div className="md:hidden fixed bottom-20 right-4 z-50">
          <Button
            onClick={openCreateModal}
            className="rounded-full w-14 h-14 shadow-lg bg-blue-600 hover:bg-blue-700"
            data-testid="new-invoice-fab-btn"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </div>

        {/* Desktop Add Button */}
        <div className="hidden md:flex justify-end">
          <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Fattura
          </Button>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingInvoice ? 'Modifica Fattura' : 'Nuova Fattura'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Numero e Data */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Numero Fattura *</label>
                    <Input
                      value={formData.numero || ''}
                      onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                      placeholder="2026/0001"
                      data-testid="invoice-numero-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Data Emissione *</label>
                    <Input
                      type="date"
                      value={formData.data_emissione || ''}
                      onChange={(e) => setFormData({ ...formData, data_emissione: e.target.value })}
                      data-testid="invoice-data-input"
                    />
                  </div>
                </div>

                {/* Cliente */}
                <div className="border rounded-lg p-3 space-y-3 bg-gray-50">
                  <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Dati Cliente
                  </h4>
                  
                  {/* Ricerca Ospite */}
                  <div className="relative">
                    <label className="text-sm text-gray-600 block mb-1">
                      <Search className="w-3 h-3 inline mr-1" />
                      Cerca ospite esistente
                    </label>
                    <div className="relative">
                      <Input
                        value={guestSearch}
                        onChange={(e) => setGuestSearch(e.target.value)}
                        placeholder="Digita nome o email ospite..."
                        className="pr-8"
                        data-testid="guest-search-input"
                      />
                      {searchingGuests && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Dropdown risultati */}
                    {showGuestDropdown && guestResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {guestResults.map((guest, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => selectGuest(guest)}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 border-b last:border-b-0 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-medium text-gray-800">{guest.nome}</div>
                                <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                                  {guest.email && <span>{guest.email}</span>}
                                  {guest.tipo === 'checkin' && (
                                    <span className="bg-green-100 text-green-700 px-1 rounded">Check-in</span>
                                  )}
                                  {guest.codice_fiscale && (
                                    <span className="text-gray-400">CF: {guest.codice_fiscale}</span>
                                  )}
                                </div>
                              </div>
                              {guest.soggiorni && guest.soggiorni.length > 0 && (
                                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                                  {guest.soggiorni.length} soggiorn{guest.soggiorni.length === 1 ? 'o' : 'i'}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {showGuestDropdown && guestResults.length === 0 && guestSearch.length >= 2 && !searchingGuests && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-gray-500">
                        Nessun ospite trovato per &quot;{guestSearch}&quot;
                      </div>
                    )}
                  </div>
                  
                  {/* Lista soggiorni selezionabili */}
                  {selectedGuestSoggiorni.length > 0 && (
                    <div className="border border-blue-200 bg-blue-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-blue-800 mb-2">
                        Seleziona un soggiorno per compilare automaticamente:
                      </p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedGuestSoggiorni.map((soggiorno, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => selectSoggiorno(soggiorno)}
                            className="w-full text-left p-2 bg-white border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-medium text-gray-800">
                                  {soggiorno.data_arrivo ? new Date(soggiorno.data_arrivo).toLocaleDateString('it-IT') : 'N/D'}
                                  {soggiorno.data_partenza && ` - ${new Date(soggiorno.data_partenza).toLocaleDateString('it-IT')}`}
                                </span>
                                <p className="text-xs text-gray-500">
                                  {soggiorno.notti || 1} notte/i - {soggiorno.struttura || 'Appartamento'}
                                </p>
                              </div>
                              <div className="text-right">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${soggiorno.is_direct ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {soggiorno.canale || 'DIRETTO'}
                                </span>
                                <p className="font-bold text-gray-800 mt-1">€ {(soggiorno.importo_netto || 0).toFixed(2)}</p>
                                {soggiorno.commissione_ota > 0 && (
                                  <p className="text-xs text-gray-400">(lordo: €{(soggiorno.importo_lordo || 0).toFixed(2)})</p>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedGuestSoggiorni([])}
                        className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                      >
                        ✕ Chiudi selezione soggiorni
                      </button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="text-sm text-gray-600 block mb-1">Nome/Ragione Sociale *</label>
                      <Input
                        value={formData.cliente_nome || ''}
                        onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })}
                        placeholder="Mario Rossi / Azienda Srl"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm text-gray-600 block mb-1">Indirizzo</label>
                      <Input
                        value={formData.cliente_indirizzo || ''}
                        onChange={(e) => setFormData({ ...formData, cliente_indirizzo: e.target.value })}
                        placeholder="Via Roma 1, 00100 Roma"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-1">Codice Fiscale</label>
                      <Input
                        value={formData.cliente_cf || ''}
                        onChange={(e) => setFormData({ ...formData, cliente_cf: e.target.value.toUpperCase() })}
                        placeholder="RSSMRA80A01H501A"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-1">Partita IVA</label>
                      <Input
                        value={formData.cliente_piva || ''}
                        onChange={(e) => setFormData({ ...formData, cliente_piva: e.target.value })}
                        placeholder="01234567890"
                      />
                    </div>
                  </div>
                </div>

                {/* Descrizione e Importi */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Descrizione *</label>
                  <textarea
                    value={formData.descrizione || ''}
                    onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 h-20"
                    placeholder="Soggiorno dal ... al ... - Appartamento..."
                    data-testid="invoice-descrizione-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Imponibile *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.importo || ''}
                      onChange={(e) => setFormData({ ...formData, importo: e.target.value })}
                      placeholder="0.00"
                      data-testid="invoice-importo-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">IVA</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.iva || ''}
                      onChange={(e) => setFormData({ ...formData, iva: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Totale</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={(parseFloat(formData.importo || 0) + parseFloat(formData.iva || 0)).toFixed(2)}
                      disabled
                      className="bg-gray-100 font-bold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Stato</label>
                    <select
                      value={formData.stato || 'bozza'}
                      onChange={(e) => setFormData({ ...formData, stato: e.target.value })}
                      className="w-full border rounded-md px-3 py-2"
                    >
                      {INVOICE_STATUS.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Data Scadenza</label>
                    <Input
                      type="date"
                      value={formData.data_scadenza || ''}
                      onChange={(e) => setFormData({ ...formData, data_scadenza: e.target.value })}
                    />
                  </div>
                </div>

                {/* Modalità di Pagamento */}
                <div>
                  <label className="text-sm text-gray-600 block mb-1">Modalità di Pagamento *</label>
                  <select
                    value={formData.modalita_pagamento || 'contanti'}
                    onChange={(e) => setFormData({ ...formData, modalita_pagamento: e.target.value })}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    {PAYMENT_METHODS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                {/* Acconti */}
                <div className="border rounded-lg p-3 space-y-3 bg-amber-50">
                  <h4 className="font-medium text-sm text-amber-800 flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Acconto Versato (opzionale)
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm text-gray-600 block mb-1">Importo Acconto</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.acconto_importo || ''}
                        onChange={(e) => setFormData({ ...formData, acconto_importo: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-1">Data Acconto</label>
                      <Input
                        type="date"
                        value={formData.acconto_data || ''}
                        onChange={(e) => setFormData({ ...formData, acconto_data: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 block mb-1">Metodo Acconto</label>
                      <select
                        value={formData.acconto_metodo || ''}
                        onChange={(e) => setFormData({ ...formData, acconto_metodo: e.target.value })}
                        className="w-full border rounded-md px-3 py-2"
                      >
                        <option value="">-- Seleziona --</option>
                        {PAYMENT_METHODS.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {formData.acconto_importo && parseFloat(formData.acconto_importo) > 0 && (
                    <div className="text-sm text-amber-700 font-medium">
                      Saldo da pagare: € {((parseFloat(formData.importo || 0) + parseFloat(formData.iva || 0)) - parseFloat(formData.acconto_importo || 0)).toFixed(2)}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-1">Note</label>
                  <textarea
                    value={formData.note || ''}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 h-16"
                    placeholder="Note interne..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowModal(false)} data-testid="invoice-cancel-btn">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="invoice-save-btn">
                    {saving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Salva
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Preview Modal - Layout FattureinCloud */}
        {showPreview && previewInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
              <CardContent className="p-6">
                {/* Header: Logo + Azienda */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    {aziendaSettings.azienda_logo_url && (
                      <img 
                        src={aziendaSettings.azienda_logo_url} 
                        alt="Logo" 
                        className="h-14 object-contain"
                      />
                    )}
                  </div>
                  <div className="text-right text-sm">
                    <h3 className="font-bold text-gray-800 uppercase">{aziendaSettings.azienda_nome || 'Configura dati'}</h3>
                    {aziendaSettings.azienda_sottotitolo && (
                      <p className="text-gray-600">{aziendaSettings.azienda_sottotitolo}</p>
                    )}
                    <p className="text-gray-500 text-xs">
                      {aziendaSettings.azienda_indirizzo} - {aziendaSettings.azienda_cap} - {aziendaSettings.azienda_citta} ({aziendaSettings.azienda_provincia})
                    </p>
                    <p className="text-gray-500 text-xs">
                      Tel: {aziendaSettings.azienda_telefono} | {aziendaSettings.azienda_email}
                    </p>
                  </div>
                </div>
                
                {/* Numero Ricevuta */}
                <div className="text-right mb-2">
                  <h2 className="text-lg">
                    <span className="font-bold">RICEVUTA FISCALE</span>
                  </h2>
                  <p className="text-blue-600 font-medium">nr. {previewInvoice.numero}</p>
                  <p className="text-sm text-gray-500">del {formatDate(previewInvoice.data_emissione)}</p>
                </div>
                
                {/* Linea oro */}
                <div className="border-t-2 border-amber-500 mb-4"></div>
                
                {/* CF Emittente + Destinatario */}
                <div className="flex justify-between mb-6">
                  <div className="text-xs text-gray-500">
                    <p>CF: {aziendaSettings.azienda_codice_fiscale}</p>
                    {(previewInvoice.cliente_cf || previewInvoice.cliente_piva) && (
                      <p className="mt-1">CF/PIVA: {previewInvoice.cliente_cf}{previewInvoice.cliente_piva ? `/${previewInvoice.cliente_piva}` : ''}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 uppercase">Destinatario</p>
                    <p className="font-bold text-gray-800">{previewInvoice.cliente_nome}</p>
                    {previewInvoice.cliente_indirizzo && (
                      <p className="text-sm text-gray-600 whitespace-pre-line">{previewInvoice.cliente_indirizzo}</p>
                    )}
                    {previewInvoice.cliente_cf && (
                      <p className="text-xs text-gray-500">C.F.: {previewInvoice.cliente_cf}</p>
                    )}
                  </div>
                </div>
                
                {/* Tabella Servizi */}
                <div className="border rounded-lg overflow-hidden mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 text-xs text-gray-500 font-normal">COD</th>
                        <th className="text-left p-2 text-xs text-gray-500 font-normal">DESCRIZIONE</th>
                        <th className="text-right p-2 text-xs text-gray-500 font-normal">IMPORTO</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-amber-50 border border-amber-200">
                        <td className="p-3">1</td>
                        <td className="p-3">
                          <span className="font-medium">1 PERN 1 AD {formatDate(previewInvoice.data_emissione)}</span>
                          <br/>
                          <span className="text-gray-600">{previewInvoice.descrizione?.toUpperCase()}</span>
                        </td>
                        <td className="p-3 text-right text-amber-600 font-bold">{formatCurrency(previewInvoice.importo)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                {/* Modalità pagamento + Acconti + Stato */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-xs text-gray-400">MODALITÀ DI PAGAMENTO</p>
                    <p className="font-medium">{PAYMENT_METHODS.find(p => p.value === previewInvoice.modalita_pagamento)?.label || previewInvoice.modalita_pagamento || 'CONTANTI'}</p>
                  </div>
                  <span className={`px-3 py-1 rounded font-bold ${previewInvoice.stato === 'pagata' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {previewInvoice.stato === 'pagata' ? 'SALDATO' : 'DA SALDARE'}
                  </span>
                </div>

                {/* Acconti se presenti */}
                {previewInvoice.acconto_importo > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-xs text-amber-600 font-medium mb-1">ACCONTO VERSATO</p>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-amber-700">{formatCurrency(previewInvoice.acconto_importo)}</p>
                        <p className="text-xs text-gray-500">
                          {previewInvoice.acconto_data && `il ${formatDate(previewInvoice.acconto_data)}`}
                          {previewInvoice.acconto_metodo && ` - ${PAYMENT_METHODS.find(p => p.value === previewInvoice.acconto_metodo)?.label || previewInvoice.acconto_metodo}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">SALDO DOVUTO</p>
                        <p className="font-bold text-lg">{formatCurrency(previewInvoice.saldo_dovuto || (previewInvoice.totale - previewInvoice.acconto_importo))}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Linea separatrice */}
                <div className="border-t border-gray-200 mb-4"></div>
                
                {/* Riepilogo IVA */}
                <div className="flex justify-between text-sm mb-2">
                  <div className="text-gray-500">
                    <p className="text-xs">RIEPILOGO IVA</p>
                    <p>Aliquota 0% - Imponibile: {formatCurrency(previewInvoice.importo)} - Imposte: € 0,00</p>
                  </div>
                </div>
                
                {/* Totale grande */}
                <div className="text-right mb-6">
                  <p className="text-sm text-gray-500">Imponibile: {formatCurrency(previewInvoice.importo)}</p>
                  <p className="text-sm text-gray-500">IVA: {formatCurrency(previewInvoice.iva)}</p>
                  <p className="text-3xl font-bold text-amber-500 mt-2">
                    TOTALE: {formatCurrency(previewInvoice.totale)}
                  </p>
                  {previewInvoice.acconto_importo > 0 && (
                    <>
                      <p className="text-sm text-gray-500 mt-2">Acconto versato: -{formatCurrency(previewInvoice.acconto_importo)}</p>
                      <p className="text-xl font-bold text-gray-700">
                        SALDO: {formatCurrency(previewInvoice.saldo_dovuto || (previewInvoice.totale - previewInvoice.acconto_importo))}
                      </p>
                    </>
                  )}
                </div>

                {/* Note se presenti */}
                {previewInvoice.note && (
                  <div className="bg-gray-50 border rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-400 uppercase mb-1">NOTE</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{previewInvoice.note}</p>
                  </div>
                )}
                
                {/* Footer */}
                <div className="border-t pt-3 text-center text-xs text-gray-400">
                  <p>{aziendaSettings.azienda_sito_web} | {aziendaSettings.azienda_email} | Tel: {aziendaSettings.azienda_telefono}</p>
                </div>
                
                {/* Pulsanti */}
                <div className="flex justify-between items-center pt-4 mt-4 border-t">
                  <Button variant="outline" onClick={() => setShowPreview(false)}>
                    Chiudi
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setShowPreview(false); openEmailModal(previewInvoice); }}>
                      <Mail className="w-4 h-4 mr-1" />
                      Invia Email
                    </Button>
                    <Button variant="outline" onClick={() => handleDownloadPDF(previewInvoice)}>
                      <Download className="w-4 h-4 mr-1" />
                      Scarica PDF
                    </Button>
                    <Button onClick={() => window.print()}>
                      <Printer className="w-4 h-4 mr-1" />
                      Stampa
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Modal Invio Email */}
        {showEmailModal && emailInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Invia Fattura via Email
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <strong>Fattura:</strong> {emailInvoice.numero}<br />
                  <strong>Cliente:</strong> {emailInvoice.cliente_nome}<br />
                  <strong>Totale:</strong> {formatCurrency(emailInvoice.totale)}
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-1">Email Destinatario *</label>
                  <Input
                    type="email"
                    value={emailForm.email}
                    onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                    placeholder="cliente@email.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-1">Oggetto</label>
                  <Input
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600 block mb-1">Messaggio</label>
                  <textarea
                    value={emailForm.message}
                    onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                    rows={8}
                    className="w-full px-3 py-2 border rounded-md resize-none text-sm"
                  />
                </div>

                <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
                  <strong>Nota:</strong> La fattura in formato PDF verrà allegata automaticamente all'email.
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowEmailModal(false)} disabled={sendingEmail}>
                    Annulla
                  </Button>
                  <Button 
                    onClick={sendInvoiceEmail} 
                    disabled={sendingEmail || !emailForm.email}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sendingEmail ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Invia Email
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
